import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Permission, Role, RoleType, SystemModule, PermissionAction } from '../models/role.models';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RoleService {
  constructor(private http: HttpClient) {}

  getRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(`${environment.apiUrl}roles`)
      .pipe(
        catchError(() => {
          // Fallback para desarrollo con datos de prueba
          return of(this.getMockRoles());
        })
      );
  }

  getRole(id: number): Observable<Role> {
    return this.http.get<Role>(`${environment.apiUrl}roles/${id}`)
      .pipe(
        catchError(() => {
          // Fallback para desarrollo
          const mockRole = this.getMockRoles().find(r => r.id === id);
          return mockRole ? of(mockRole) : of({
            id: 0,
            name: 'Role no encontrado',
            description: '',
            permissions: []
          });
        })
      );
  }

  createRole(role: Omit<Role, 'id'>): Observable<Role> {
    return this.http.post<Role>(`${environment.apiUrl}roles`, role);
  }

  updateRole(id: number, role: Partial<Role>): Observable<Role> {
    return this.http.patch<Role>(`${environment.apiUrl}roles/${id}`, role);
  }

  deleteRole(id: number): Observable<boolean> {
    return this.http.delete<any>(`${environment.apiUrl}roles/${id}`)
      .pipe(map(() => true));
  }

  getPermissions(): Observable<Permission[]> {
    return this.http.get<Permission[]>(`${environment.apiUrl}permissions`)
      .pipe(
        catchError(() => {
          // Fallback para desarrollo
          return of(this.getMockPermissions());
        })
      );
  }

  // Métodos auxiliares para desarrollo

  private getMockRoles(): Role[] {
    return [
      {
        id: 1,
        name: RoleType.ADMIN,
        description: 'Acceso total a todos los módulos',
        permissions: this.getMockPermissions().filter(p => p.name === 'all:all'),
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 2,
        name: RoleType.PRODUCTION,
        description: 'Gestión de producción y consulta de stock',
        permissions: this.getProductionPermissions(),
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 3,
        name: RoleType.SALES,
        description: 'Gestión de ventas y consulta de productos y stock',
        permissions: this.getSalesPermissions(),
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 4,
        name: RoleType.PURCHASES,
        description: 'Gestión de compras y consulta de productos y stock',
        permissions: this.getPurchasesPermissions(),
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 5,
        name: RoleType.CASHIER,
        description: 'Gestión de movimientos de caja y consulta de ventas',
        permissions: this.getCashierPermissions(),
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 6,
        name: RoleType.SUPERVISOR,
        description: 'Acceso de lectura a todos los módulos',
        permissions: this.getSupervisorPermissions(),
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 7,
        name: RoleType.SUPPORT,
        description: 'Acceso a configuración y ayuda',
        permissions: this.getSupportPermissions(),
        createdAt: new Date(),
        updatedAt: new Date()
      }
    ];
  }

  private getMockPermissions(): Permission[] {
    const permissions: Permission[] = [
      // Permiso administrador
      { id: 1, name: 'all:all', description: 'Acceso total al sistema', module: '*', action: 'write' as 'write' },

      // Permisos por módulo
      ...Object.keys(SystemModule).map((key, index) => {
        const module = SystemModule[key];
        const modulePermissions = [
          { 
            id: 100 + index * 3, 
            name: `${module}:read`, 
            description: `Acceso de lectura a ${module}`, 
            module, 
            action: 'read' as 'read' 
          },
          { 
            id: 101 + index * 3, 
            name: `${module}:write`, 
            description: `Acceso de escritura a ${module}`, 
            module, 
            action: 'write' as 'write' 
          },
          { 
            id: 102 + index * 3, 
            name: `${module}:delete`, 
            description: `Acceso para eliminar en ${module}`, 
            module, 
            action: 'delete' as 'delete' 
          }
        ] as Permission[];
        return modulePermissions;
      }).reduce((acc, modulePerms) => acc.concat(modulePerms), [])
    ];

    return permissions;
  }

  private getProductionPermissions(): Permission[] {
    return this.getMockPermissions().filter(p => 
      p.module === SystemModule.PRODUCTION && (p.action === 'read' || p.action === 'write' || p.action === 'delete') ||
      p.module === SystemModule.STOCK && p.action === 'read'
    );
  }

  private getSalesPermissions(): Permission[] {
    return this.getMockPermissions().filter(p => 
      p.module === SystemModule.SALES && (p.action === 'read' || p.action === 'write' || p.action === 'delete') ||
      p.module === SystemModule.PRODUCTS && p.action === 'read' ||
      p.module === SystemModule.STOCK && p.action === 'read' ||
      p.module === SystemModule.CLIENTS && (p.action === 'read' || p.action === 'write')
    );
  }

  private getPurchasesPermissions(): Permission[] {
    return this.getMockPermissions().filter(p => 
      p.module === SystemModule.PURCHASES && (p.action === 'read' || p.action === 'write' || p.action === 'delete') ||
      p.module === SystemModule.PRODUCTS && p.action === 'read' ||
      p.module === SystemModule.STOCK && p.action === 'read'
    );
  }

  private getCashierPermissions(): Permission[] {
    return this.getMockPermissions().filter(p => 
      p.module === SystemModule.CASH && (p.action === 'read' || p.action === 'write') ||
      p.module === SystemModule.SALES && p.action === 'read'
    );
  }

  private getSupervisorPermissions(): Permission[] {
    // Solo lectura en todos los módulos
    return this.getMockPermissions().filter(p => p.action === 'read');
  }

  private getSupportPermissions(): Permission[] {
    return this.getMockPermissions().filter(p => 
      p.module === SystemModule.SETTINGS && (p.action === 'read' || p.action === 'write') ||
      p.module === SystemModule.HELP && (p.action === 'read' || p.action === 'write')
    );
  }
}